import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Badge,
  Progress,
  Space,
  Empty,
  Select,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTargetSummary, upsertTarget, getCategories } from '../api/budget';

interface BudgetTarget {
  category: string;
  target_amount: number;
  actual_spent: number;
  remaining: number;
  over_budget: boolean;
}

interface TableRow extends BudgetTarget {
  key: string;
}

const MonthlyBudgets: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [data, setData] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [form] = Form.useForm();

  const loadBudgetData = async () => {
    setLoading(true);
    try {
      const [summaryResp, catResp] = await Promise.all([
        getTargetSummary(selectedMonth.year(), selectedMonth.month() + 1),
        getCategories(),
      ]);
      if (summaryResp.ok && summaryResp.data) {
        const rows: TableRow[] = (summaryResp.data as unknown as BudgetTarget[]).map((item) => ({
          ...item,
          key: item.category,
        }));
        setData(rows);
      }
      if (catResp.ok && catResp.data) {
        setAllCategories(catResp.data.spend ?? []);
      }
    } catch (error) {
      message.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBudgetData();
  }, [selectedMonth]);

  const handleSetTarget = (category: string) => {
    setSelectedCategory(category);
    setNewCategoryMode(false);
    const row = data.find((r) => r.key === category);
    form.setFieldsValue({
      target_amount: row?.target_amount || 0,
    });
    setIsModalVisible(true);
  };

  const handleAddNewCategory = () => {
    setSelectedCategory('');
    setNewCategoryMode(true);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (newCategoryMode && !selectedCategory) {
        message.error('Please select a category');
        return;
      }

      const category = newCategoryMode ? selectedCategory : selectedCategory;
      if (!category) {
        message.error('Category is required');
        return;
      }

      await upsertTarget({
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
        category,
        target_amount: parseFloat(values.target_amount),
      });

      message.success('Budget target updated');
      setIsModalVisible(false);
      form.resetFields();
      await loadBudgetData();
    } catch (error) {
      message.error('Failed to update budget target');
    }
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
    },
    {
      title: 'Target Amount',
      dataIndex: 'target_amount',
      key: 'target_amount',
      width: 130,
      render: (amount) => `$${amount.toFixed(2)}`,
    },
    {
      title: 'Actual Spent',
      dataIndex: 'actual_spent',
      key: 'actual_spent',
      width: 130,
      render: (amount) => `$${amount.toFixed(2)}`,
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining',
      key: 'remaining',
      width: 130,
      render: (amount) => (
        <span style={{ color: amount >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ${amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'over_budget',
      key: 'over_budget',
      width: 100,
      render: (overBudget) => (
        <Badge
          status={overBudget ? 'error' : 'success'}
          text={overBudget ? 'Over Budget' : 'On Track'}
        />
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 150,
      render: (_, record) => {
        const percentage = record.target_amount
          ? Math.min((record.actual_spent / record.target_amount) * 100, 100)
          : 0;
        return (
          <Progress
            percent={Math.round(percentage)}
            strokeColor={record.over_budget ? '#ff4d4f' : '#52c41a'}
            size="small"
          />
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleSetTarget(record.key)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const unusedCategories = allCategories.filter(
    (cat) => !data.some((d) => d.category === cat)
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Monthly Budgets</h1>
        <Space>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(date) => setSelectedMonth(date || dayjs())}
          />
          {unusedCategories.length > 0 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNewCategory}
            >
              Add Category Target
            </Button>
          )}
        </Space>
      </div>

      {data.length === 0 ? (
        <Empty description="No budget data available" />
      ) : (
        <Table<TableRow>
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          bordered
        />
      )}

      <Modal
        title={
          newCategoryMode
            ? 'Add Category Budget Target'
            : `Set Budget Target - ${selectedCategory}`
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical">
          {newCategoryMode && (
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select
                placeholder="Select a category"
                onChange={setSelectedCategory}
              >
                {unusedCategories.map((cat) => (
                  <Select.Option key={cat} value={cat}>
                    {cat}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="target_amount"
            label="Target Amount ($)"
            rules={[
              { required: true, message: 'Please enter target amount' },
              {
                pattern: /^\d+(\.\d{1,2})?$/,
                message: 'Please enter a valid amount',
              },
            ]}
          >
            <Input type="number" placeholder="0.00" step="0.01" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MonthlyBudgets;
